import { useEffect, useRef, useState } from 'react'
import type { Message } from './types/portal'
import { comoSeDibuja } from './burbujas'

/**
 * El mensaje NUEVO DEL CLIENTE que acaba de llegar al hilo abierto, para que
 * su burbuja entre con animación en vez de aparecer de golpe.
 *
 * Hasta el 06-oct-2026 este hook también hacía sonar un aviso y escribía
 * «(N)» en el título de la pestaña. Las dos cosas estaban repetidas: el Layout
 * ya suena con el contador del sidebar y ya pone el número en el título, así
 * que un mensaje sonaba DOS veces —una por cada sondeo— y el título lo
 * escribían dos dueños que se pisaban («(1) (2) Portal PGH»). Hoy suena uno
 * solo, el del Layout, con la preferencia del botón «Avisos».
 *
 * DOS REGLAS QUE PARECEN DETALLES Y NO LO SON
 *
 * 1. Solo los mensajes del CLIENTE. Los del asesor son propios y los del bot
 *    no son algo que haya que mirar llegar.
 * 2. NUNCA en la primera carga. Abrir una conversación con 40 mensajes
 *    animaría el último, que puede ser de ayer.
 */
export function useAvisoMensajes(
  conversationId: string | null,
  messages: Message[] | undefined,
): string | null {
  // Último mensaje del cliente ya visto por el hook. `null` = todavía no se
  // hizo la primera carga de esta conversación.
  const ultimoVisto = useRef<string | null>(null)
  const [reciente, setReciente] = useState<string | null>(null)

  // Cambiar de conversación reinicia todo: el primer render de la nueva no
  // tiene que animar nada.
  useEffect(() => {
    ultimoVisto.current = null
    setReciente(null)
  }, [conversationId])

  useEffect(() => {
    if (!messages?.length) return
    // Lo que el cliente escribió, no lo que el bot anotó de él (DEF-079): la
    // anotación no se dibuja y no tiene burbuja que resaltar.
    const delCliente = messages.filter((m) => m.sender === 'user' && comoSeDibuja(m) !== 'nada')
    const ultimo = delCliente[delCliente.length - 1]?.message_id ?? ''

    // Primera carga de esta conversación: se toma nota y no se anima.
    if (ultimoVisto.current === null) {
      ultimoVisto.current = ultimo
      return
    }
    if (!ultimo || ultimo === ultimoVisto.current) return

    ultimoVisto.current = ultimo
    setReciente(ultimo)
  }, [messages])

  return reciente
}
